import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const css = `
.dash-page { display:flex; flex-direction:column; gap:14px; }

/* Stats */
.dash-stats { display:grid; grid-template-columns:repeat(auto-fit,minmax(160px,1fr)); gap:10px; }
.dash-stat  { background:var(--bg); border:0.5px solid var(--border); border-radius:10px;
              padding:12px 14px; display:flex; align-items:center; gap:10px; }
.ds-ico     { width:38px; height:38px; border-radius:10px; display:flex; align-items:center;
              justify-content:center; font-size:17px; flex-shrink:0; }
.ds-n       { font-size:20px; font-weight:500; line-height:1.1; }
.ds-l       { font-size:11px; color:var(--text3); margin-top:1px; }

/* Layout */
.dash-main  { display:grid; grid-template-columns:1fr 320px; gap:14px; }
.dash-card  { background:var(--bg); border:0.5px solid var(--border); border-radius:10px; overflow:hidden; }
.dc-hdr     { padding:10px 14px; display:flex; align-items:center; justify-content:space-between;
              border-bottom:0.5px solid var(--border); }
.dc-ttl     { font-size:13px; font-weight:500; }

/* Tabla turnos */
.t-head     { display:grid; grid-template-columns:32px 1fr 90px 90px 140px;
              gap:8px; padding:6px 14px; background:var(--surface);
              font-size:10px; font-weight:600; color:var(--text3);
              text-transform:uppercase; letter-spacing:.4px; }
.t-row      { display:grid; grid-template-columns:32px 1fr 90px 90px 140px;
              gap:8px; align-items:center; padding:9px 14px;
              border-bottom:0.5px solid var(--border); transition:background .1s; }
.t-row:hover{ background:var(--surface); }
.t-row:last-child { border-bottom:none; }
.t-num      { width:28px; height:28px; border-radius:7px; display:flex; align-items:center;
              justify-content:center; font-size:11px; font-weight:500; }
.t-pac      { min-width:0; }
.t-nom      { font-size:12px; font-weight:500; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; max-width:100%; }
.t-sub      { font-size:10px; color:var(--text3); }
.t-acts     { display:flex; gap:4px; }

/* Badges */
.badge      { font-size:10px; padding:2px 8px; border-radius:20px; font-weight:500;
              white-space:nowrap; display:inline-flex; align-items:center; gap:3px; }
.b-prg      { background:#EEF6FF; color:#1D6A96; }
.b-atn      { background:#FFF8E6; color:#B45309; }
.b-fin      { background:#EAF3DE; color:#27500A; }
.b-anl      { background:#FEF2F2; color:#991B1B; }
.b-pag      { background:#EAF3DE; color:#27500A; }
.b-pen      { background:#FEF9EC; color:#B45309; }
.b-nof      { background:var(--surface); color:var(--text3); }

/* Botones */
.btn-sm     { font-size:10px; padding:4px 8px; border:0.5px solid var(--border); border-radius:6px;
              background:transparent; color:var(--text2); cursor:pointer; white-space:nowrap; }
.btn-sm:hover { background:var(--surface); }
.btn-sm.pri { background:#534AB7; color:#fff; border-color:#534AB7; }
.btn-sm.grn { background:#1D9E75; color:#fff; border-color:#1D9E75; }
.btn-sm.pri:hover { background:#3C3489; }
.btn-sm.grn:hover { background:#0F6E56; }

/* Progreso */
.prog-row   { display:flex; align-items:center; gap:8px; margin-bottom:7px; }
.prog-lbl   { font-size:11px; color:var(--text2); width:72px; flex-shrink:0; }
.prog-bar   { flex:1; height:6px; background:var(--surface); border-radius:3px; overflow:hidden; }
.prog-fill  { height:100%; border-radius:3px; transition:width .5s; }
.prog-val   { font-size:10px; color:var(--text3); min-width:32px; text-align:right; }

/* Barras chart */
.chart-wrap { display:flex; align-items:flex-end; gap:4px; height:70px; padding:0 2px; }
.bar-g      { flex:1; display:flex; flex-direction:column; align-items:center; gap:3px; }
.bar-b      { width:100%; border-radius:3px 3px 0 0; min-height:4px; }
.bar-l      { font-size:8px; color:var(--text3); }

/* Accesos */
.quick-grid { display:grid; grid-template-columns:1fr 1fr; gap:6px; padding:10px 14px; }
.quick-btn  { padding:9px; font-size:11px; text-align:center; border:0.5px solid var(--border);
              border-radius:8px; background:transparent; cursor:pointer; color:var(--text2);
              display:flex; align-items:center; justify-content:center; gap:5px; }
.quick-btn:hover { background:var(--surface); color:var(--text1); }

/* Vacío */
.empty-dash { padding:32px; text-align:center; color:var(--text3); font-size:13px; }

/* Responsive */
@media (max-width: 720px) {
  .dash-main { grid-template-columns: 1fr; }
  .t-head { display:none; }
  .t-row {
    grid-template-columns: 32px 1fr;
    grid-template-areas: "num pac" "num estado" "num factura" "num acts";
    row-gap:4px;
  }
  .t-num { grid-area: num; }
  .t-pac { grid-area: pac; }
  .t-row > .badge:nth-of-type(1) { grid-area: estado; justify-self:start; }
  .t-row > .badge:nth-of-type(2) { grid-area: factura; justify-self:start; }
  .t-acts { grid-area: acts; flex-wrap:wrap; }
}
`;

const ESTADO_TURNO = {
  PRG: { label:'Programado',  cls:'b-prg' },
  ATN: { label:'En atención', cls:'b-atn' },
  FIN: { label:'Atendido',    cls:'b-fin' },
  ANL: { label:'Anulado',     cls:'b-anl' },
  AUS: { label:'Ausente',     cls:'b-anl' },
};

const ESTADO_FAC = (t) => {
  if (!t.COD_FACTURA) return { label:'Sin factura', cls:'b-nof' };
  // ACT = factura activa (emitida), PAG = pagada, cualquier otra = pendiente
  if (['ACT','PAG'].includes(t.ESTADO_FACTURA)) return { label:'Facturado', cls:'b-pag' };
  return { label:'Pendiente', cls:'b-pen' };
};

export default function Dashboard() {
  const navigate  = useNavigate();
  const fechaLocalHoy = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  };
  const hoy       = fechaLocalHoy();
  const [fecha, setFecha]       = useState(hoy);
  const [medico, setMedico]     = useState('');  // se sobreescribe en useEffect
  const [medicos, setMedicos]   = useState([]);
  const [resumen, setResumen]   = useState(null);
  const [turnos, setTurnos]     = useState([]);
  const [meses, setMeses]       = useState([]);
  const [loading, setLoading]   = useState(false);
  const [hora, setHora]         = useState(new Date().toLocaleTimeString('es-EC'));
  const [svModal, setSvModal]   = useState(null); // {cod, nombre} del turno seleccionado para signos vitales
  const { usuario = {} }        = useAuth();
  // esDBA = administrador (ve todos), esMedico = solo sus turnos
  const esMedico = usuario.esMedico === true && !usuario.esDBA;

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [r, t, m] = await Promise.all([
        api.get(`/dashboard/resumen?fecha=${fecha}${medico?`&medico=${medico}`:''}`),
        api.get(`/dashboard/proximos-turnos?fecha=${fecha}${medico?`&medico=${medico}`:''}`),
        api.get(`/dashboard/consultas-mes?meses=6`),
      ]);
      setResumen(r.data);
      setTurnos(t.data);
      setMeses(m.data);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [fecha, medico]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => {
    const t = setInterval(() => setHora(new Date().toLocaleTimeString('es-EC')), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    // Si es médico, filtrar solo sus turnos automáticamente
    if (esMedico && usuario.usuario) {
      setMedico(usuario.usuario.toUpperCase());
    }
    // Solo admin carga lista de médicos
    if (!esMedico) {
      api.get('/configuracion/usuarios').then(r => {
        setMedicos((r.data || []).filter(u => u.TIPO_USUARIO === 'MEDICO'));
      }).catch(() => {});
    }
  }, []);

  const atendidos   = resumen?.turnos?.atendidos   || 0;
  const programados = resumen?.turnos?.programados  || 0;
  const total       = resumen?.turnos?.total        || 0;
  const facturados  = turnos.filter(t => t.COD_FACTURA).length;
  const maxMes      = Math.max(...meses.map(m => m.total || 0), 1);

  const irHistoria    = (cod) => navigate(`/historia?paciente=${cod}`);
  // Vintage es una clínica exclusivamente odontológica -- "Atender" debe
  // llevar directo al Odontograma (donde el dentista realmente trabaja),
  // no a Historia clínica general. "Historia" se deja aparte por si se
  // necesita revisar antecedentes.
  const irOdontograma = (cod) => navigate(`/odontograma?paciente=${cod}`);
  const irFactura     = ()    => navigate('/facturacion');

  return (
    <>
      <style>{css}</style>
      <div className="dash-page fade-up">

        {/* Encabezado bienvenida */}
        <div style={{background:'#1a2535',borderRadius:10,padding:'16px 22px',
          display:'flex',alignItems:'center',justifyContent:'space-between',
          color:'#fff',marginBottom:2}}>
          <div>
            <div style={{fontSize:10,color:'#8899AA',letterSpacing:'.5px',
              textTransform:'uppercase',marginBottom:4}}>
              Bienvenido al sistema
            </div>
            <div style={{fontSize:18,fontWeight:500,marginBottom:3}}>
              {usuario.nombres || usuario.usuario || 'Doctor'}
            </div>
            <div style={{fontSize:11,color:'#AAB8CC'}}>
              {usuario.usuario?.toUpperCase()}

              &nbsp;·&nbsp;
              {new Date(fecha+'T12:00:00').toLocaleDateString('es-EC',
                {weekday:'long',day:'numeric',month:'long',year:'numeric'})}
            </div>
          </div>
          <div style={{textAlign:'right'}}>
            <div style={{fontSize:32,fontWeight:300,letterSpacing:1,color:'#fff'}}>
              {hora}
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
          <input type="date" className="form-control" style={{maxWidth:160}}
            value={fecha} onChange={e=>setFecha(e.target.value)}/>
          {!esMedico && (
            <select className="form-control" style={{maxWidth:200}}
              value={medico} onChange={e=>setMedico(e.target.value)}>
              <option value="">Todos los médicos</option>
              {medicos.map(m=><option key={m.USUARIO} value={m.USUARIO}>{m.NOMBRES}</option>)}
            </select>
          )}
          <button className="btn btn-sm" onClick={cargar}>↺ Actualizar</button>
          <div style={{marginLeft:'auto',fontSize:12,color:'var(--text3)'}}>
            {new Date(fecha+'T12:00:00').toLocaleDateString('es-EC',{weekday:'long',year:'numeric',month:'long',day:'numeric'})}
          </div>
        </div>

        {/* Stats */}
        <div className="dash-stats">
          {[
            { ico:'📅', bg:'#EEEDFE', n: total,                           l:'Turnos hoy' },
            { ico:'✅', bg:'#EAF3DE', n: atendidos,                        l:'Atendidos' },
            { ico:'⏳', bg:'#FFF8E6', n: programados,                      l:'Pendientes' },
            { ico:'💰', bg:'#EAF3DE', n:`$${Number(resumen?.facturacion?.total||0).toFixed(2)}`, l:'Facturado hoy' },
          ].map((s,i)=>(
            <div key={i} className="dash-stat">
              <div className="ds-ico" style={{background:s.bg}}>{s.ico}</div>
              <div><div className="ds-n">{loading?'…':s.n}</div><div className="ds-l">{s.l}</div></div>
            </div>
          ))}
        </div>

        {/* Grid principal */}
        <div className="dash-main">

          {/* Tabla turnos */}
          <div className="dash-card">
            <div className="dc-hdr">
              <span className="dc-ttl">Turnos del día ({turnos.length})</span>
              <button className="btn-sm pri" onClick={()=>navigate('/turnos')}>+ Nuevo turno</button>
            </div>
            <div className="t-head">
              <span>#</span><span>Paciente</span>
              <span>Estado</span><span>Factura</span><span>Acciones</span>
            </div>
            {loading
              ? <div className="empty-dash">Cargando...</div>
              : turnos.length === 0
                ? <div className="empty-dash">Sin turnos para esta fecha</div>
                : turnos.map(t => {
                    const est  = ESTADO_TURNO[t.C_ESTADO] || { label:t.C_ESTADO, cls:'b-nof' };
                    const fac  = ESTADO_FAC(t);
                    const numBg = t.C_ESTADO==='ATN' ? '#FEF3C7' :
                                  t.C_ESTADO==='FIN' ? '#EAF3DE' :
                                  t.C_ESTADO==='ANL' ? '#FEF2F2' : 'var(--surface)';
                    const numCl = t.C_ESTADO==='ATN' ? '#B45309' :
                                  t.C_ESTADO==='FIN' ? '#27500A' :
                                  t.C_ESTADO==='ANL' ? '#991B1B' : 'var(--text3)';
                    return (
                      <div key={t.COD} className="t-row"
                        style={t.C_ESTADO==='ATN'?{background:'#FFFBEA'}:{}}>
                        <div className="t-num" style={{background:numBg,color:numCl}}>{t.NUMERO}</div>
                        <div className="t-pac">
                          <div className="t-nom">{t.PACIENTE?.trim()}</div>
                          <div className="t-sub">
                            {t.IDENTIFICACION}
                            {t.EDAD ? ` · ${t.EDAD} años` : ''}
                            {t.HORA ? ` · ${t.HORA}` : ''}
                            {t.SERVICIO ? ` · ${t.SERVICIO}` : ''}
                          </div>
                        </div>
                        <span className={`badge ${est.cls}`}>{est.label}</span>
                        <span className={`badge ${fac.cls}`}>{fac.label}</span>
                        <div className="t-acts">
                          {!['ANL','AUS'].includes(t.C_ESTADO) && (
                            <button className="btn-sm"
                              style={{color:'#B45309',borderColor:'#FDE68A'}}
                              onClick={()=>setSvModal({cod:t.COD_PACIENTE, nombre:t.PACIENTE?.trim()})}>
                              ❤ Vitales
                            </button>
                          )}
                          {!['ATN','FIN','ANL','AUS'].includes(t.C_ESTADO) && (
                            <button className="btn-sm pri"
                              onClick={()=>irOdontograma(t.COD_PACIENTE)}>
                              Atender
                            </button>
                          )}
                          {(t.C_ESTADO === 'ATN' || t.C_ESTADO === 'FIN') && (
                            <button className="btn-sm"
                              onClick={()=>irHistoria(t.COD_PACIENTE)}>
                              Historia
                            </button>
                          )}
                          {(t.C_ESTADO === 'ATN' || t.C_ESTADO === 'FIN') && !t.COD_FACTURA && (
                            <button className="btn-sm grn"
                              onClick={()=>navigate(`/facturacion?paciente=${t.COD_PACIENTE}&turno=${t.COD}`)}>
                              Facturar
                            </button>
                          )}
                          {(t.C_ESTADO === 'ATN' || t.C_ESTADO === 'FIN') && t.COD_FACTURA && (
                            <button className="btn-sm"
                              style={{color:'#27500A',borderColor:'#86EFAC'}}
                              onClick={()=>navigate('/facturacion')}>
                              Ver factura
                            </button>
                          )}
                          {t.COD_FACTURA && (
                            <button className="btn-sm" onClick={irFactura}>
                              Ver factura
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
          </div>

          {/* Columna derecha */}
          <div style={{display:'flex',flexDirection:'column',gap:12}}>

            {/* Progreso */}
            <div className="dash-card">
              <div className="dc-hdr"><span className="dc-ttl">Progreso del día</span></div>
              <div style={{padding:'12px 14px'}}>
                {[
                  { l:'Atendidos',   v:atendidos,  t:total, c:'#1D9E75' },
                  { l:'Facturados',  v:facturados, t:total, c:'#534AB7' },
                ].map((p,i)=>(
                  <div key={i} className="prog-row" style={i===1?{marginBottom:0}:{}}>
                    <div className="prog-lbl">{p.l}</div>
                    <div className="prog-bar">
                      <div className="prog-fill"
                        style={{width:`${total?Math.round(p.v/total*100):0}%`,background:p.c}}/>
                    </div>
                    <div className="prog-val">{p.v}/{total}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Gráfico meses */}
            <div className="dash-card">
              <div className="dc-hdr"><span className="dc-ttl">Consultas últimos 6 meses</span></div>
              <div style={{padding:'10px 14px 8px'}}>
                <div className="chart-wrap">
                  {meses.map((m,i)=>(
                    <div key={i} className="bar-g">
                      <div className="bar-b"
                        style={{height:`${Math.round((m.total/maxMes)*60)}px`,
                                background:'#534AB7',
                                opacity: i===meses.length-1 ? 0.5 : 1}}/>
                      <div className="bar-l">{m.label?.slice(0,3)}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Accesos rápidos */}
            <div className="dash-card">
              <div className="dc-hdr"><span className="dc-ttl">Accesos rápidos</span></div>
              <div className="quick-grid">
                {[
                  ['📋','Nueva consulta', '/odontograma'],
                  ['📅','Ver agenda',     '/turnos'],
                  ['💰','Facturación',    '/facturacion'],
                  ['👥','Pacientes',      '/pacientes'],
                ].map(([ico,lbl,path])=>(
                  <button key={path} className="quick-btn" onClick={()=>navigate(path)}>
                    <span style={{fontSize:14}}>{ico}</span> {lbl}
                  </button>
                ))}
              </div>
            </div>

          </div>
        </div>
      </div>
      {svModal && (
        <SignosVitalesModal
          paciente={svModal}
          onClose={()=>setSvModal(null)}
          onSaved={()=>{ setSvModal(null); cargar(); }}
        />
      )}
    </>
  );
}
function SignosVitalesModal({ paciente, onClose, onSaved }) {
  const [f, setF]         = useState({ PRESION_ARTERIAL:'', PULSO:'', SATURACION_O2:'', PESO:'', TALLA:'', TEMP_AXILAR:'' });
  const [saving, setSaving] = useState(false);
  const [err, setErr]     = useState('');
  const campo = (k,label,ph) => (
    <label style={{display:'flex',flexDirection:'column',gap:4,fontSize:12}}>
      {label}
      <input value={f[k]} onChange={e=>setF({...f,[k]:e.target.value})} placeholder={ph}
        style={{border:'1px solid var(--border)',borderRadius:8,padding:'8px 10px',fontSize:13}} />
    </label>
  );
  const guardar = async () => {
    setSaving(true); setErr('');
    try {
      await api.post('/historia/signos-vitales', { COD_PACIENTE: paciente.cod, ...f });
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.error || 'No se pudo guardar');
    } finally { setSaving(false); }
  };
  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.4)',display:'flex',
      alignItems:'center',justifyContent:'center',zIndex:1000}} onClick={onClose}>
      <div style={{background:'#fff',borderRadius:12,padding:'20px 22px',width:380,maxWidth:'92vw'}}
        onClick={e=>e.stopPropagation()}>
        <div style={{fontSize:14,fontWeight:600,marginBottom:2}}>Signos vitales</div>
        <div style={{fontSize:12,color:'var(--text3)',marginBottom:14}}>{paciente.nombre}</div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
          {campo('PRESION_ARTERIAL','P. Arterial','120/80')}
          {campo('PULSO','Pulso','72')}
          {campo('SATURACION_O2','Sat. O2 (%)','98')}
          {campo('PESO','Peso (kg)','70')}
          {campo('TALLA','Talla (cm)','165')}
          {campo('TEMP_AXILAR','Temp. (°C)','36.5')}
        </div>
        {err && <div style={{color:'#B91C1C',fontSize:12,marginTop:10}}>{err}</div>}
        <div style={{display:'flex',justifyContent:'flex-end',gap:8,marginTop:16}}>
          <button className="btn-sm" onClick={onClose}>Cancelar</button>
          <button className="btn-sm pri" disabled={saving} onClick={guardar}>
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}
